import React, { memo, useState, useEffect, useMemo, useCallback } from "react";
import { array, string } from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import { getAllHistoryLabelTypes } from "redux/labelingHistory/selector";
import { selectImageId } from "redux/image/selectors";
import { setLabelingHistoryDisplay } from "redux/labelingHistory/actions";

import parentStyle from "../../style.module.scss";
import HistoryTimeline from "./HistoryTimeline";
import CompareSection from "./CompareSection";
import HistoryShapeList from "./HistoryShapeList";
import { getComparisons, isSameId, sortByCreatedDateDesc } from "./utils";

export default memo(HistoryTab);

HistoryTab.propTypes = {
  activeTab: string,
  labelingHistories: array,
};

function HistoryTab({ activeTab, labelingHistories = [] }) {
  const dispatch = useDispatch();
  const labelTypes = useSelector(getAllHistoryLabelTypes);
  const imageId = useSelector(selectImageId);

  const [selectedLabelId, setSelectedLabelId] = useState("");
  const [selectedHistoryId, setSelectedHistoryId] = useState("");

  const histories = useMemo(() => sortByCreatedDateDesc(labelingHistories), [
    labelingHistories,
  ]);

  const selectedHistory = useMemo(
    () => histories.find((history) => history.historyId === selectedHistoryId),
    [histories, selectedHistoryId]
  );

  // Compare results may contain labels the labeler never drew (missing),
  // add them so the Select always has a matching option.
  const labelOptions = useMemo(() => {
    const options = [...labelTypes];
    getComparisons(selectedHistory).forEach((comparison) => {
      (comparison.result?.labelTypes || []).forEach((item) => {
        if (!options.some((type) => isSameId(type.labelId, item.labelId))) {
          options.push({
            labelId: String(item.labelId),
            labelTypeName: item.labelTypeName,
          });
        }
      });
    });
    return options;
  }, [labelTypes, selectedHistory]);

  const shapesByLabel = useMemo(() => {
    const labelingData = selectedHistory?.labelingData || [];

    return labelingData
      .filter(
        (item) => !selectedLabelId || isSameId(item.labelId, selectedLabelId)
      )
      .reduce((acc, item) => {
        const labelPoints = item?.labelPoints || [];
        acc[item.labelId] = labelPoints.map((labelPoint) => ({
          classesTypeCode: item.classesTypeCode,
          labelName: item.labelTypeName,
          labelId: item.labelId,
          color: item.labelTypeColor || "rgb(189, 253, 162)",
          id: labelPoint.labelPointId,
          dataPoint: labelPoint.dataPoint,
        }));
        return acc;
      }, {});
  }, [selectedHistory, selectedLabelId]);

  useEffect(() => {
    const allPoints = Object.values(shapesByLabel).flat();
    const displayingShapes = allPoints.map((point) => ({
      labelId: point.labelId,
      id: point.id,
    }));
    const payload = { displayingShapes, shapes: allPoints, shapesByLabel };

    dispatch(setLabelingHistoryDisplay(payload));
  }, [shapesByLabel]);

  // Sync selectedHistoryId to the latest history when list loads/changes.
  useEffect(() => {
    // Keep the user's selection if it still exists (e.g. after a refetch)
    setSelectedHistoryId((prev) =>
      histories.some((history) => history.historyId === prev)
        ? prev
        : histories[0]?.historyId || ""
    );
  }, [histories]);

  // Reset selectedLabelId when the image changes
  useEffect(() => {
    setSelectedLabelId("");
  }, [imageId]);

  // Clicking a row in the compare table toggles the label filter.
  // Use the value from labelTypes so it matches the Select's MenuItem value.
  const handleSelectCompareLabel = useCallback(
    (labelId) => {
      if (isSameId(labelId, selectedLabelId)) {
        setSelectedLabelId("");
        return;
      }
      const labelType = labelOptions.find((type) =>
        isSameId(type.labelId, labelId)
      );
      setSelectedLabelId(labelType ? labelType.labelId : String(labelId));
    },
    [labelOptions, selectedLabelId]
  );

  return (
    <div
      className={parentStyle.tabContent}
      style={activeTab !== "history" ? { display: "none" } : {}}
    >
      <div className={parentStyle.historyContainer}>
        <HistoryTimeline
          histories={histories}
          selectedHistoryId={selectedHistoryId}
          onSelect={setSelectedHistoryId}
        />

        <CompareSection
          history={selectedHistory}
          histories={histories}
          selectedLabelId={selectedLabelId}
          onSelectLabel={handleSelectCompareLabel}
          onViewHistory={setSelectedHistoryId}
        />

        <HistoryShapeList
          labelTypes={labelOptions}
          shapesByLabel={shapesByLabel}
          selectedLabelId={selectedLabelId}
          onSelectLabel={setSelectedLabelId}
        />
      </div>
    </div>
  );
}
